/**
 * Use the Compact Hero with Promo Rail to deliver relevant and personalized promotional content in a concise, visually appealing format.
 *
 * @module views/components/ShowCaseHero
 * @memberof -Common
 */
import './ShowCaseHero.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { constants } from '@ulta/core/utils/constants/constants';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

/**
 * Represents a ShowCaseHero component
 *
 * @method
 * @param {object} props - React properties passed from composition. See ShowCaseHeroProps.
 * @returns ShowCaseHero
 */
export const ShowCaseHero = function( props ){
  const {
    heroSlot,
    railSlot,
    root = defaultProps.root,
    rootMargin = defaultProps.rootMargin,
    threshold = defaultProps.threshold
  } = props;
  const showCaseHeroRef = useRef( null );

  if( !heroSlot || !railSlot ){
    return null;
  }

  useIntersectionObserver(
    showCaseHeroRef,
    {
      root: root,
      rootMargin: rootMargin,
      threshold: threshold
    },
    handleIntersection( props )
  );

  return (
    <div ref={ showCaseHeroRef } className='ShowCaseHero'>
      <div className='ShowCaseHero__heroSlot'>
        <AsyncComponent
          { ...spreadProps( { ...heroSlot } ) }
        />
      </div>
      <div className='ShowCaseHero__railSlot'>
        <AsyncComponent
          { ...spreadProps( { ...railSlot } ) }
          isGrid={ false }
          title={ null }
        />
      </div>
    </div>
  );
};

/**
 * Property type definitions
 *
 * @typedef ShowCaseHeroProps
 * @type {object}
 * @property {object} heroSlot - sets FullBleedHeroCompact
 * @property {object} railSlot - sets PromoGroup Rail
 */
export const propTypes = {
  heroSlot: PropTypes.object,
  railSlot: PropTypes.object
};

/**
 * Default minimum heights for the ShowCaseHero loader ghost card at each breakpoint.
 * Applied when the component is in a loading state.
 *
 * @const
 * @type {object}
 * @property {string} xl - Minimum height for extra-large viewports
 * @property {string} lg - Minimum height for large viewports
 * @property {string} md - Minimum height for medium viewports
 * @property {string} sm - Minimum height for small viewports
 */
export const SHOWCASE_HERO_MIN_HEIGHT = {
  xl: '32.375rem', // 518px
  lg: '32.9375rem', // 527px
  md: '30.125rem', // 482px
  sm: '34.4375rem' // 551px
};

/**
 * Default values for passed properties
 * @type object
 */
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

ShowCaseHero.defaultProps = defaultProps;
ShowCaseHero.propTypes = propTypes;
ShowCaseHero.minHeights = SHOWCASE_HERO_MIN_HEIGHT;

export default ShowCaseHero;